import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, Calendar, ChevronRight, Clock, Copy, 
  Share2, Link2, Tag, FileText, MessageSquare, Bookmark,
  CheckCircle2, ArrowUp, ChevronDown, AlertTriangle, Info, Eye
} from 'lucide-react';

// Import the standalone components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';
import Button from './Button';
import Contact from './Contact';

// --- Enhanced Color Palette from main site ---
const COLORS = {
  primary: '#ce2223',       // Lusidea Red
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',  
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',     // Accent Yellow/Gold
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb', 
  secondaryLighter: '#fffbf0',
  black: '#111827',         
  darkGray: '#374151',      
  mediumGray: '#6b7280',    
  lightGray: '#e5e7eb',     
  offWhite: '#f9fafb',      
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

// --- Improved Spacing System matching main site ---
const SPACING = {
  xs: 'px-3 py-1.5',
  sm: 'px-4 py-2.5',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-16 md:py-24 lg:py-28',
  sectionMedium: 'py-12 md:py-18 lg:py-22',
  sectionCompact: 'py-8 md:py-14 lg:py-18',
  container: 'max-w-7xl mx-auto px-5 sm:px-8 md:px-10 lg:px-12 xl:px-8'
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [copied, setCopied] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [expandedImageUrl, setExpandedImageUrl] = useState(null);
  const [warningMessage, setWarningMessage] = useState(null);
  
  // Calculate read time function - handles any content format
  const calculateReadTime = useCallback((content) => {
    if (!content) return 1;
    
    // Handle different content types
    let text = content;
    if (typeof content === 'string') {
      // Remove HTML tags if content is HTML
      text = content.replace(/<[^>]*>/g, '');
    }
    
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(wordCount / 225)); // Minimum 1 minute
  }, []);

  // Extract headings for table of contents - handles various heading formats
  const extractHeadings = useCallback((content) => {
    if (!content) return [];
    
    const headings = [];
    
    // Try different heading formats
    const patterns = [
      /<h2.*?>(.*?)<\/h2>/g,                    // Standard h2
      /<h([1-3]).*?>(.*?)<\/h\1>/g,             // Any h1-h3 as fallback
      /<([^>]*class="[^"]*heading[^"]*"[^>]*)>(.*?)<\/[^>]*>/g // Class-based headings
    ];
    
    // Try each pattern until we find headings
    for (const pattern of patterns) {
      let match;
      const tempHeadings = [];
      
      while ((match = pattern.exec(content)) !== null) {
        // If it's the h1-h3 pattern, we need the heading level and text
        const headingText = match[2] ? match[2] : match[1];
        const rawText = headingText;
        
        // Clean HTML tags from text
        const text = rawText.replace(/<[^>]*>/g, '').trim();
        if (!text) continue; // Skip empty headings
        
        // Generate an ID for the heading
        const id = text
          .toLowerCase()
          .replace(/[^\w\s]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+$/, ''); // Trim trailing dashes
          
        tempHeadings.push({ text, id });
      }
      
      if (tempHeadings.length > 0) {
        headings.push(...tempHeadings);
        break; // Stop after finding headings with first successful pattern
      }
    }
    
    return headings;
  }, []);
  
  // Extract tags from post content - adaptable to different content formats
  const extractTags = useCallback((content, category) => {
    if (!content) return [category].filter(Boolean);
    
    // Try different patterns for finding tags
    const patterns = [
      /<strong.*?>(.*?)<\/strong>/g,   // Standard strong tags
      /<b.*?>(.*?)<\/b>/g,             // Bold tags as alternative
      /<em.*?>(.*?)<\/em>/g,           // Emphasis tags as last resort
      /<span.*?class="[^"]*tag[^"]*"[^>]*>(.*?)<\/span>/g // Class-based tags
    ];
    
    let allMatches = [];
    
    // Try each pattern
    for (const pattern of patterns) {
      const matches = [];
      let match;
      
      while ((match = pattern.exec(content)) !== null) {
        matches.push(match[1]);
      }
      
      if (matches.length > 0) {
        allMatches.push(...matches);
      }
    }
    
    // Process matches
    const possibleTags = allMatches
      .map(tag => tag.replace(/<[^>]*>/g, '').trim())
      .filter(tag => 
        tag.length > 3 && 
        tag.length < 30 && 
        !tag.includes('<') && 
        !tag.includes('>')
      )
      .slice(0, 8); // Get up to 8 potential tags
      
    // Deduplicate and combine with category
    const allTags = category ? [category, ...possibleTags] : possibleTags;
    return [...new Set(allTags)].filter(Boolean);
  }, []);
  
  // Extract FAQ questions and answers - handles different FAQ formats
  const extractFaqs = useCallback((content) => {
    if (!content) return [];
    
    const faqs = [];
    
    // Try different FAQ patterns
    const patterns = [
      { 
        pattern: /<h4.*?>(.*?)<\/h4>\s*<p.*?>(.*?)<\/p>/g,
        questionIndex: 1,
        answerIndex: 2
      },
      { 
        pattern: /<h3.*?>(.*?)<\/h3>\s*<p.*?>(.*?)<\/p>/g,
        questionIndex: 1,
        answerIndex: 2
      },
      { 
        pattern: /<dt.*?>(.*?)<\/dt>\s*<dd.*?>(.*?)<\/dd>/g,
        questionIndex: 1,
        answerIndex: 2
      },
      {
        pattern: /<p.*?><strong.*?>(.*?)\?<\/strong><\/p>\s*<p.*?>(.*?)<\/p>/g,
        questionIndex: 1,
        answerIndex: 2
      }
    ];
    
    // Check for FAQ section markers
    const faqSectionPatterns = [
      /<h2.*?>(?:.*?FAQ.*?|.*?Frequently Asked Questions.*?|.*?Questions.*?)<\/h2>/i,
      /<section.*?(?:class|id)="[^"]*faq[^"]*"[^>]*>/i
    ];
    
    let faqSectionExists = false;
    for (const pattern of faqSectionPatterns) {
      if (pattern.test(content)) {
        faqSectionExists = true;
        break;
      }
    }
    
    // If there's no explicit FAQ section, don't try to extract FAQs
    if (!faqSectionExists) return [];
    
    // Try each pattern
    for (const { pattern, questionIndex, answerIndex } of patterns) {
      let match;
      const tempFaqs = [];
      
      while ((match = pattern.exec(content)) !== null) {
        const question = match[questionIndex].replace(/<[^>]*>/g, '').trim();
        // Keep HTML in the answer for formatting
        const answer = match[answerIndex];
        
        if (question && answer) {
          tempFaqs.push({ question, answer });
        }
      }
      
      if (tempFaqs.length > 0) {
        faqs.push(...tempFaqs);
        break; // Stop after first successful pattern
      }
    }
    
    return faqs;
  }, []);
  
  // Extract related links/resources
  const extractRelatedLinks = useCallback((content) => {
    if (!content) return [];
    
    // Check for related links section markers
    const sectionPatterns = [
      /<h2.*?>(?:.*?Additional Read.*?|.*?Further Reading.*?|.*?Related.*?|.*?Resources.*?)<\/h2>/i,
      /<section.*?(?:class|id)="[^"]*related[^"]*"[^>]*>/i,
      /<section.*?(?:class|id)="[^"]*resources[^"]*"[^>]*>/i
    ];
    
    let sectionExists = false;
    let sectionContent = '';
    
    for (const pattern of sectionPatterns) {
      const match = pattern.exec(content);
      if (match) {
        sectionExists = true;
        // Extract content after this section
        const afterSection = content.substring(match.index + match[0].length);
        
        // Find the end of the section (next h2 or end of content)
        const nextSectionMatch = /<h2.*?>/.exec(afterSection);
        
        if (nextSectionMatch) {
          sectionContent = afterSection.substring(0, nextSectionMatch.index);
        } else {
          sectionContent = afterSection;
        }
        
        break;
      }
    }
    
    if (!sectionExists) return [];
    
    // Extract links from the section content
    const links = [];
    const linkPattern = /<a\s+(?:[^>]*?\s+)?href="([^"]*)"(?:\s+[^>]*?)?>(.+?)<\/a>/g;
    
    let linkMatch;
    while ((linkMatch = linkPattern.exec(sectionContent)) !== null) {
      const url = linkMatch[1];
      const text = linkMatch[2].replace(/<[^>]*>/g, '').trim();
      
      // Check if it's a valid URL
      if (url && text && (url.startsWith('http') || url.startsWith('/'))) {
        links.push({ url, text });
      }
    }
    
    return links;
  }, []);

  // Fetch blog post data
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    fetch('/data/blog.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch blog data');
        }
        return response.json();
      })
      .then(data => {
        // Handle both array and object formats for blog data
        const posts = Array.isArray(data) ? data : [data];
        
        // Find the current post
        const currentPost = posts.find(p => p['Blog Post - Link'] === slug);
        
        if (!currentPost) {
          throw new Error('Post not found');
        }
        
        setPost(currentPost);
        
        // Find related posts (same category, excluding current post)
        const related = posts
          .filter(p => 
            p['New Category'] === currentPost['New Category'] && 
            p['Blog Post - Link'] !== slug
          )
          .sort((a, b) => new Date(b['Published On'] || 0) - new Date(a['Published On'] || 0))
          .slice(0, 3);
          
        setRelatedPosts(related);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching blog data:', err);
        setError(err.message);
        setLoading(false);
      });
      
    window.scrollTo(0, 0);
  }, [slug]);

  // Reading progress tracker and active section detection
  useEffect(() => {
    const handleScroll = () => {
      // Update reading progress
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      const scrollPercent = scrollTop / (docHeight - winHeight);
      setReadingProgress(scrollPercent * 100);
      
      // Update active section for table of contents
      // Query all heading elements that have an ID
      const headingElements = Array.from(document.querySelectorAll('[id]')).filter(
        el => el.tagName && /^H[1-6]$/i.test(el.tagName)
      );
      
      if (headingElements.length === 0) return;
      
      // Find the currently visible heading
      for (let i = headingElements.length - 1; i >= 0; i--) {
        const heading = headingElements[i];
        const rect = heading.getBoundingClientRect();
        
        if (rect.top <= 150) {
          setActiveSection(heading.id);
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update SEO meta tags - moved to top level to follow hooks rules
  useEffect(() => {
    if (!post) return; // Skip if post isn't loaded yet
    
    const getPostData = (key, fallback = '') => {
      return post ? post[key] || fallback : fallback;
    };
    
    // Update document title
    document.title = `${getPostData('Blog Post - Title')} | Lusidea Blog`;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = getPostData('Blog Post - Short Description');
    
    // Add canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = `https://lusidea.com/blog/${getPostData('Blog Post - Link')}`;
    
    // Clean up on component unmount
    return () => {
      document.title = 'Lusidea';
    };
  }, [post]);

  // Copy article URL to clipboard
  const copyToClipboard = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err);
        setWarningMessage('Unable to copy URL. Please try again.');
        setTimeout(() => setWarningMessage(null), 3000);
      });
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primaryLight via-white to-secondaryLight">
        <div className="animate-pulse flex flex-col items-center">
          <div className="rounded-full bg-primary/20 h-24 w-24 flex items-center justify-center mb-6">
            <FileText className="h-12 w-12 text-primary animate-pulse" />
          </div>
          <p className="text-xl font-medium text-darkGray">Loading article...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primaryLight via-white to-secondaryLight">
        <div className="text-center max-w-md mx-auto">
          <h2 className="text-3xl font-bold text-black mb-4">
            {error === 'Post not found' ? 'Article Not Found' : 'Something Went Wrong'}
          </h2>
          <p className="text-lg text-darkGray mb-8">
            {error === 'Post not found' 
              ? "We couldn't find the article you're looking for." 
              : "We're having trouble loading this article."}
          </p>
          <Button 
            href="/blog" 
            variant="primary" 
            icon={ArrowLeft}
            className="shadow-md hover:shadow-lg transition-shadow"
          >
            Return to Blog
          </Button>
        </div>
      </div>
    );
  }

  // Safely get post data with fallbacks
  const getPostData = (key, fallback = '') => {
    return post[key] || fallback;
  };

  // Format date for display with fallback
  const published = (() => {
    try {
      const dateStr = getPostData('Published On');
      if (!dateStr) return 'Recently published';
      
      return new Date(dateStr)
        .toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Recently published';
    }
  })();
    
  // Calculate read time
  const readTime = calculateReadTime(getPostData('Blog Post - Richt Text'));
  
  // Extract headings for table of contents
  const headings = extractHeadings(getPostData('Blog Post - Richt Text'));
  
  // Extract tags
  const tags = extractTags(getPostData('Blog Post - Richt Text'), getPostData('New Category'));
  
  // Extract FAQs
  const faqs = extractFaqs(getPostData('Blog Post - Richt Text'));
  
  // Extract related links
  const relatedLinks = extractRelatedLinks(getPostData('Blog Post - Richt Text'));
  
  // Check if table of contents should be shown
  const showTableOfContents = headings.length >= 2;

  // --- Breadcrumb Navigation ---
  const SubpageNav = () => (
    <div className="bg-gradient-to-r from-offWhite to-white py-3 border-b border-lightGray/30">
      <div className={`${SPACING.container} flex items-center text-sm overflow-x-auto whitespace-nowrap py-2`}>
        {[
          {label: 'Home', href: '/'},
          {label: 'Blog', href: '/blog'},
          {label: getPostData('New Category', 'Category'), href: `/blog/category/${getPostData('New Category', 'category').toLowerCase().replace(/\s+/g, '-')}`},
          {label: getPostData('Blog Post - Title'), href: null}
        ].map((crumb, i, arr) => (
          <React.Fragment key={i}>
            {crumb.href
              ? <a href={crumb.href} className="text-mediumGray hover:text-primary transition-colors">{crumb.label}</a>
              : <span className="text-primary font-semibold truncate max-w-xs">{crumb.label}</span>}
            {i < arr.length - 1 && <ChevronRight size={14} className="mx-2 text-lightGray flex-shrink-0"/>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // --- Progress Bar ---
  const ProgressBar = () => (
    <div className="fixed top-0 left-0 w-full h-1 bg-lightGray z-50">
      <div 
        className="h-full bg-primary transition-all duration-300 ease-out"
        style={{ width: `${readingProgress}%` }}
      ></div>
    </div>
  );

  // --- Blog Post Header with Compact Cover ---
  const BlogPostHeader = () => {
    // Determine category color based on category name
    const categoryColor = "primary"; // Fallback to primary color
    
    return (
      <section className="pt-28 pb-8 relative overflow-hidden">
        {/* Subtle background with gradient */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primaryLighter via-white to-secondaryLighter opacity-70"></div>
        
        <div className={`${SPACING.container} max-w-4xl mx-auto relative z-10`}>
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mb-5">
            <a 
              href={`/blog/category/${getPostData('New Category', 'category').toLowerCase().replace(/\s+/g, '-')}`}
              className="inline-block bg-primaryLight px-5 py-1.5 rounded-full text-primary font-semibold text-sm hover:bg-primary hover:text-white transition-colors"
            >
              {getPostData('New Category', 'Blog')}
            </a>
            <div className="flex items-center text-mediumGray text-sm">
              <Calendar size={14} className="mr-1.5"/> {published}
            </div>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-5 leading-tight">
            {getPostData('Blog Post - Title', 'Article')}
          </h1>
          
          {/* Compact article metadata bar */}
          <div className="flex items-center justify-between mb-6 py-4 border-y border-lightGray">
            {/* Left: Article metadata */}
            <div className="flex flex-wrap items-center gap-5 text-mediumGray">
              <div className="flex items-center">
                <Clock size={16} className="mr-2"/>
                <span className="font-medium">{readTime} min read</span>
              </div>
              
              <div className="flex items-center">
                <Eye size={16} className="mr-2"/>
                <span>Recently published</span>
              </div>
            </div>
            
            {/* Right: Action buttons */}
            <div className="flex items-center gap-2">
              <button 
                className="p-2 rounded-full bg-offWhite hover:bg-primaryLight text-mediumGray hover:text-primary transition-colors"
                onClick={copyToClipboard}
                aria-label="Copy link to clipboard"
                title="Copy link"
              >
                <Link2 size={16}/>
              </button>
              
              <button 
                className="p-2 rounded-full bg-offWhite hover:bg-primaryLight text-mediumGray hover:text-primary transition-colors"
                aria-label="Save article"
                title="Save article"
              >
                <Bookmark size={16}/>
              </button>
              
              <button 
                className="p-2 rounded-full bg-offWhite hover:bg-primaryLight text-mediumGray hover:text-primary transition-colors"
                aria-label="Share article"
                title="Share article"
              >
                <Share2 size={16}/>
              </button>
            </div>
          </div>
          
          {/* Cover Graphic - minimal and content-specific */}
          <div className="relative h-16 sm:h-20 mb-8 flex items-center overflow-hidden rounded-lg bg-gradient-to-r from-primary to-primaryDark shadow-md">
            {/* Topic icon */}
            <div className="absolute left-6 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            
            {/* Title visual */}
            <div className="ml-20 text-white">
              <div className="font-bold">Article</div>
              <div className="text-xs text-white/80">{getPostData('New Category', '')} Series</div>
            </div>
            
            {/* Decorative pattern */}
            <div className="absolute right-0 inset-y-0 w-1/3 opacity-10">
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
                <pattern id="smallGrid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
                <rect width="100%" height="100%" fill="url(#smallGrid)" />
              </svg>
            </div>
          </div>
          
          {/* Notification system */}
          {copied && (
            <div className="fixed bottom-6 right-6 bg-success text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center animate-fade-in">
              <CheckCircle2 size={16} className="mr-2" />
              Link copied to clipboard!
            </div>
          )}
          
          {warningMessage && (
            <div className="fixed bottom-6 right-6 bg-yellow-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center animate-fade-in">
              <AlertTriangle size={16} className="mr-2" />
              {warningMessage}
            </div>
          )}
          
          {expandedImageUrl && (
            <div 
              className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-pointer"
              onClick={() => setExpandedImageUrl(null)}
            >
              <div className="relative max-w-4xl max-h-[90vh] w-full">
                <img 
                  src={expandedImageUrl} 
                  alt="Expanded view" 
                  className="w-full h-full object-contain"
                />
                <button 
                  className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedImageUrl(null);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  };

  // --- Blog Post Content with Table of Contents ---
  const BlogPostContent = () => {
    // Table of Contents component
    const TableOfContents = () => {
      if (!showTableOfContents) return null;
      
      return (
        <div className="hidden lg:block sticky top-24 self-start bg-white rounded-xl shadow-md border border-lightGray/30 p-6 max-w-xs">
          <h3 className="text-lg font-bold text-black mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-primary">
              <line x1="21" y1="10" x2="7" y2="10"></line>
              <line x1="21" y1="6" x2="3" y2="6"></line>
              <line x1="21" y1="14" x2="3" y2="14"></line>
              <line x1="21" y1="18" x2="7" y2="18"></line>
            </svg>
            In This Article
          </h3>
          
          <nav aria-label="Table of contents">
            <ul className="space-y-3">
              {headings.map((heading, index) => (
                <li key={index}>
                  <a 
                    href={`#${heading.id}`} 
                    className={activeSection === heading.id ? "text-primary font-semibold" : "text-mediumGray"}
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById(heading.id);
                      if (element) {
                        const yOffset = -100; 
                        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                        window.scrollTo({top: y, behavior: 'smooth'});
                      }
                    }}
                  >
                    <div className="flex items-center group">
                      <div className={`w-6 h-6 rounded-full ${activeSection === heading.id ? 'bg-primaryLight' : 'bg-lightGray'} flex items-center justify-center mr-3 group-hover:bg-primaryLight transition-colors`}>
                        <span className="text-xs font-medium">{index + 1}</span>
                      </div>
                      <span className="text-sm">{heading.text.replace(/<[^>]*>/g, '')}</span>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      );
    };
    
    // FAQ Section Component
    const FaqSection = () => {
      if (faqs.length === 0) return null;
      
      return (
        <div className="my-10 py-8 px-6 sm:px-8 bg-offWhite rounded-xl border border-lightGray/30">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="border border-lightGray rounded-lg overflow-hidden bg-white"
              >
                <button
                  className="w-full text-left p-4 flex justify-between items-center"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  aria-expanded={openFaqIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span className="font-semibold text-lg text-black pr-8">{faq.question}</span>
                  <ChevronDown 
                    size={20} 
                    className={`text-primary transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                
                <div 
                  id={`faq-answer-${index}`}
                  className={`transition-all duration-300 overflow-hidden ${openFaqIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div 
                    className="p-4 pt-0 text-darkGray blog-content" 
                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };
    
    // Related Links Section
    const RelatedLinksSection = () => {
      if (relatedLinks.length === 0) return null;
      
      return (
        <div className="my-10 py-8 px-6 sm:px-8 bg-primaryLighter rounded-xl border border-primaryLight/30">
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <Info size={24} className="mr-3 text-primary" />
            Additional Resources
          </h2>
          
          <ul className="space-y-3">
            {relatedLinks.map((link, index) => (
              <li key={index} className="relative pl-8 py-1">
                <div className="absolute left-0 top-1.5 w-5 h-5 rounded-full bg-primaryLight flex items-center justify-center">
                  <ArrowRight size={12} className="text-primary" />
                </div>
                <a 
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium"
                >
                  {link.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      );
    };
    
    // Function to enhance HTML content for proper rendering
    const enhanceHtmlContent = (htmlContent) => {
      if (!htmlContent) return '';
      
      // Add IDs to headings for better navigation
      let processedContent = htmlContent.replace(
        /<(h[2-6])(?:\s+[^>]*id=["'][^"']*["'])?(?:\s+[^>]*)?>(.*?)<\/\1>/gi,
        (match, tag, content) => {
          // Extract existing ID if any
          const existingIdMatch = match.match(/id=["']([^"']*)["']/i);
          const existingId = existingIdMatch ? existingIdMatch[1] : '';
          
          // If there's already an ID, use that
          if (existingId) {
            return match;
          }
          
          // Otherwise, generate an ID from the content
          const text = content.replace(/<[^>]*>/g, '').trim();
          if (!text) return match;
          
          const id = text
            .toLowerCase()
            .replace(/[^\w\s]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+$/, '');
            
          return `<${tag} id="${id}">${content}</${tag}>`;
        }
      );
      
      // Process external links to open in new tabs
      processedContent = processedContent.replace(
        /<a\s+([^>]*)href=["'](http[^"']*)["']([^>]*)>(.*?)<\/a>/gi,
        '<a $1href="$2"$3 target="_blank" rel="noopener noreferrer">$4</a>'
      );
      
      return processedContent;
    };

    return (
      <section className="py-8">
        <div className={SPACING.container}>
          <div className="flex flex-col lg:flex-row gap-10">
            <TableOfContents />
            
            <article className="flex-1 max-w-4xl lg:mx-0">
              {/* Introduction */}
              <div 
                className="text-xl font-medium text-darkGray mb-8 leading-relaxed border-l-4 border-primary pl-5 py-2" 
              >
                {getPostData('Blog Post - Short Description')}
              </div>
              
              {/* Main content - FIXED RENDERING */}
              <div className="blog-content prose prose-lg max-w-none">
                <div dangerouslySetInnerHTML={{ 
                  __html: enhanceHtmlContent(getPostData('Blog Post - Richt Text', ''))
                }} />
              </div>
              
              {/* FAQ Section - rendered separately for better styling */}
              <FaqSection />
              
              {/* Related Links Section */}
              <RelatedLinksSection />
              
              {/* Custom styling for blog content */}
              <style jsx="true">{`
                /* Base content styling */
                .blog-content {
                  color: ${COLORS.darkGray};
                  line-height: 1.7;
                  font-size: 1.125rem;
                }
                
                /* Heading styles */
                .blog-content h2 {
                  font-size: 1.875rem;
                  font-weight: 700;
                  color: ${COLORS.black};
                  margin-top: 2.5rem;
                  margin-bottom: 1rem;
                  padding-bottom: 0.75rem;
                  border-bottom: 2px solid ${COLORS.lightGray};
                  scroll-margin-top: 100px;
                  position: relative;
                }
                
                .blog-content h2::before {
                  content: '';
                  position: absolute;
                  bottom: -2px;
                  left: 0;
                  width: 50px;
                  height: 2px;
                  background-color: ${COLORS.primary};
                }
                
                .blog-content h3, .blog-content h4 {
                  font-size: 1.25rem;
                  font-weight: 600;
                  color: ${COLORS.black};
                  margin-top: 1.5rem;
                  margin-bottom: 0.75rem;
                }
                
                /* Paragraph styling */
                .blog-content p {
                  margin-bottom: 1.5rem;
                }
                
                /* Text emphasis styles */
                .blog-content strong {
                  font-weight: 700;
                  color: ${COLORS.black};
                }
                
                .blog-content em {
                  font-style: italic;
                }
                
                /* Link styling */
                .blog-content a {
                  color: ${COLORS.primary};
                  text-decoration: none;
                  transition: all 0.2s;
                  font-weight: 500;
                }
                
                .blog-content a:hover {
                  text-decoration: underline;
                }
                
                /* List styling */
                .blog-content ul, .blog-content ol {
                  margin: 1.5rem 0;
                  padding-left: 1.5rem;
                }
                
                .blog-content li {
                  position: relative;
                  margin-bottom: 0.75rem;
                }
                
                .blog-content ul li::marker {
                  color: ${COLORS.primary};
                }
                
                /* Responsive adjustments */
                @media (max-width: 640px) {
                  .blog-content h2 {
                    font-size: 1.5rem;
                  }
                  
                  .blog-content h3, .blog-content h4 {
                    font-size: 1.125rem;
                  }
                }
              `}</style>
              
              {/* Tags section */}
              <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-lightGray">
                <span className="text-mediumGray flex items-center mr-2">
                  <Tag size={16} className="mr-1.5"/> Topics:
                </span>
                {tags.map((tag, i) => (
                  <a 
                    key={i} 
                    href={`/blog/tag/${tag.toLowerCase().replace(/\s+/g, '-')}`}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primaryLighter text-primary hover:bg-primaryLight transition-colors"
                  >
                    {tag}
                  </a>
                ))}
              </div>
              
              {/* Article feedback section */}
              <div className="mt-12 p-6 bg-offWhite rounded-xl border border-lightGray/30">
                <h3 className="font-bold text-lg mb-4 flex items-center">
                  <MessageSquare size={20} className="mr-2 text-primary" />
                  Was this article helpful?
                </h3>
                
                <div className="flex flex-wrap gap-3">
                  <button className="px-4 py-2 bg-white rounded-lg border border-lightGray hover:border-primary text-darkGray hover:text-primary transition-colors">
                    Very helpful
                  </button>
                  <button className="px-4 py-2 bg-white rounded-lg border border-lightGray hover:border-primary text-darkGray hover:text-primary transition-colors">
                    Somewhat helpful
                  </button>
                  <button className="px-4 py-2 bg-white rounded-lg border border-lightGray hover:border-primary text-darkGray hover:text-primary transition-colors">
                    Not helpful
                  </button>
                </div>
                
                <div className="mt-4 text-sm text-mediumGray">
                  Your feedback helps us improve our content
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
    );
  };

  // --- Related Posts Section ---
  const RelatedPostsSection = () => {
    if (relatedPosts.length === 0) return null;
    
    // Function to generate decorative cover for related posts
    const generateCover = (index) => {
      const colors = [
        'from-primaryLight to-primaryLighter', 
        'from-secondaryLight to-secondaryLighter',
        'from-offWhite to-lightGray'
      ];
      
      return colors[index % colors.length];
    };
    
    return (
      <section className="py-16 bg-gradient-to-b from-white to-offWhite">
        <div className={SPACING.container}>
          <div className="flex justify-between items-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-black">
              Related <span className="text-primary">Articles</span>
            </h2>
            <a 
              href={`/blog/category/${getPostData('New Category', 'category').toLowerCase().replace(/\s+/g, '-')}`}
              className="text-primary font-medium flex items-center hover:underline group"
            >
              More from this category <ArrowRight size={16} className="ml-1.5 group-hover:translate-x-1 transition-transform"/>
            </a>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {relatedPosts.map((r, index) => {
              const postReadTime = calculateReadTime(r['Blog Post - Richt Text'] || '');
              const postDate = (() => {
                try {
                  if (!r['Published On']) return 'Recently';
                  return new Date(r['Published On']).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  });
                } catch (e) {
                  return 'Recently';
                }
              })();
              
              return (
                <article 
                  key={index} // Use index as fallback if link is missing
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-500 border border-lightGray/40 hover:-translate-y-1 h-full flex flex-col"
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <a 
                    href={`/blog/${r['Blog Post - Link'] || `post-${index}`}`} 
                    className="h-full flex flex-col"
                    aria-label={`Read article: ${r['Blog Post - Title'] || 'Related article'}`}
                  >
                    {/* Decorative cover instead of image */}
                    <div className={`h-32 relative overflow-hidden bg-gradient-to-br ${generateCover(index)}`}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center">
                            <FileText size={16} className="text-black/70" />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-semibold text-primary bg-primaryLighter px-3 py-1 rounded-full">
                          {r['New Category'] || 'Article'}
                        </span>
                        <span className="text-xs text-mediumGray flex items-center">
                          <Calendar size={12} className="mr-1"/>
                          {postDate}
                        </span>
                      </div>
                      
                      <h3 className="text-lg font-bold text-black mb-3 hover:text-primary transition-colors line-clamp-2">
                        {r['Blog Post - Title'] || 'Related Article'}
                      </h3>
                      
                      <p className="text-sm text-darkGray line-clamp-3 mb-4 flex-grow">
                        {r['Blog Post - Short Description'] || 'Read more about this related topic.'}
                      </p>
                      
                      <div className="flex justify-between items-center">
                        <span className="inline-flex items-center text-sm font-medium text-primary group">
                          Read More <ArrowRight size={14} className="ml-1.5 group-hover:translate-x-1 transition-transform"/>
                        </span>
                        
                        <span className="text-xs text-mediumGray flex items-center">
                          <Clock size={12} className="mr-1"/>
                          {postReadTime} min read
                        </span>
                      </div>
                    </div>
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  // --- Back to Top Button ---
  const BackToTopButton = () => (
    <button
      className={`fixed bottom-6 right-6 w-10 h-10 bg-primary/80 hover:bg-primary text-white rounded-full shadow-lg flex items-center justify-center z-30 transition-opacity duration-300 ${readingProgress > 20 ? 'opacity-100' : 'opacity-0'}`}
      onClick={scrollToTop}
      aria-label="Back to top"
    >
      <ArrowUp size={18} />
    </button>
  );

  return (
    <div className="font-sans antialiased text-darkGray bg-white overflow-x-hidden">
      <HeaderNav />
      <ProgressBar />
      <SubpageNav />
      <main className="space-y-0">
        <BlogPostHeader />
        <BlogPostContent />
        <RelatedPostsSection />
        <Contact />
      </main>
      <BackToTopButton />
      <FooterLan />
    </div>
  );
};

export default BlogPostPage;